import { useEffect, useRef, useState, type RefObject } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../../Avatar';
import type { CommunicationPatientRef } from '../../../../services/communications';
import type { PatientRow } from '../../../../services/patientsV2';
import { FloatingMenu } from '../../../FloatingMenu';
import { usePatientPicker } from './usePatientPicker';

interface PatientPickerProps {
    triggerRef: RefObject<HTMLElement>;
    practiceId: string;
    linkedIds: string[];
    /** A second, separately-labelled disabled set (e.g. AddPatientNoteModal's
     *  already-noted patients) - distinct from `linkedIds`'s "Linked" badge. */
    disabledIds?: string[];
    disabledLabel?: string;
    onPick: (patient: CommunicationPatientRef) => void;
    onClose: () => void;
}

// Inline search picker opened by the Linked patients card's "+ Link" action.
// Portaled (see FloatingMenu) so it isn't clipped by the rail's overflow.
// Closes after a single pick - linking another means reopening it.
export function PatientPicker({
    triggerRef,
    practiceId,
    linkedIds,
    disabledIds = [],
    disabledLabel,
    onPick,
    onClose
}: PatientPickerProps) {
    const [search, setSearch] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const { results, isLoading } = usePatientPicker(practiceId, search, true);

    useEffect(() => {
        inputRef.current?.focus({ preventScroll: true });
    }, []);

    const handlePick = (patient: PatientRow) => {
        onPick({
            id: patient.id,
            name: patient.name,
            profileThumbnailUrl: patient.profileThumbnailUrl
        });
    };

    return (
        // No matchTriggerWidth: the "+ Add patient" trigger this also opens
        // from (AddPatientNoteModal) is a narrow, content-sized button, and
        // matching it squeezed names down to a couple of characters with a
        // horizontal scrollbar. A fixed width, like every other dropdown
        // panel in the app (AssigneeSelector's min-w-[220px], the shared
        // FilterDropdown's w-64), keeps this readable regardless of trigger size.
        <FloatingMenu open triggerRef={triggerRef} onClose={onClose} estimatedHeight={280}>
            <div
                className="bg-white border border-gray-200 rounded-xl shadow-lg p-1.5 w-[320px]"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-gray-100 mb-1">
                    <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') onClose();
                        }}
                        placeholder="Search this practice's patients"
                        className="flex-1 min-w-0 text-[13px] text-gray-700 placeholder-gray-400 outline-none bg-transparent"
                    />
                </div>
                <div className="max-h-[220px] overflow-y-auto overflow-x-hidden">
                    {isLoading && (
                        <div className="px-3 py-2 text-[12px] text-gray-400">Searching…</div>
                    )}
                    {!isLoading && results.length === 0 && (
                        <div className="px-3 py-2 text-[12px] text-gray-400">
                            No patients found.
                        </div>
                    )}
                    {!isLoading &&
                        results.map((patient) => {
                            const alreadyLinked = linkedIds.includes(patient.id);
                            const otherDisabledReason = disabledIds.includes(patient.id)
                                ? disabledLabel
                                : undefined;
                            const disabled = alreadyLinked || Boolean(otherDisabledReason);
                            return (
                                <button
                                    key={patient.id}
                                    type="button"
                                    disabled={disabled}
                                    title={otherDisabledReason}
                                    onClick={() => handlePick(patient)}
                                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2.5 ${
                                        disabled
                                            ? 'opacity-40 cursor-not-allowed'
                                            : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="w-6 h-6 shrink-0">
                                        <Avatar
                                            user={{ name: patient.name, email: '' }}
                                            thumbnailURL={patient.profileThumbnailUrl ?? undefined}
                                        />
                                    </div>
                                    <span
                                        title={patient.name}
                                        className="flex-1 min-w-0 text-[13px] font-semibold text-gray-900 truncate"
                                    >
                                        {patient.name}
                                    </span>
                                    {disabled && (
                                        <span className="ml-auto text-[11px] text-gray-400 shrink-0">
                                            {otherDisabledReason ?? 'Linked'}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                </div>
            </div>
        </FloatingMenu>
    );
}
